import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../Context/authContext';

const RouteGuard = ({ children, requireAuth = true, skipStatusChecks = false }) => {
  const { isLoggedIn, user, authReady, loading_user, allowedPages } = useContextHook(AuthContext, v => ({
    isLoggedIn: v.isLoggedIn,
    user: v.user,
    authReady: v.authReady,
    loading_user: v.loading_user,
    allowedPages: v.allowedPages,
  }));

  const location = useLocation();

  if (skipStatusChecks && isLoggedIn) {
    return children;
  }

  if (requireAuth && !isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  // Only check email verification after user data is loaded
  if (isLoggedIn && user && authReady && !loading_user) {
    const userData = user.data || user;
    const { status, isEmailVerified } = userData;
    const currentPath = location.pathname;
    const stripeSubscriptionId = userData?.subscriptionDetail?.stripeSubscriptionId;
    const hasManualSubscription =
      typeof stripeSubscriptionId === 'string' && stripeSubscriptionId.startsWith('manual_subscription');


    // Only redirect to verify-otp if user data is loaded and email is actually not verified
    if (isEmailVerified === false && currentPath !== '/verify-otp') {
      return <Navigate to="/verify-otp" replace />;
    }

    if (isEmailVerified) {
      if (status === 'signup' && !['/onboarding', '/ai-onboarding'].includes(currentPath)) {
        return <Navigate to="/onboarding" replace />;
      }


      // If user has active trial or subscription, allow access to dashboard and other protected routes
      if (status === 'trial_active' || status === 'subscription_active' || status === 'onboarding_completed') {
        // Manual subscription users should not access Teams route directly.
        if (hasManualSubscription && currentPath.startsWith('/teams')) {
          return <Navigate to="/dashboard" replace />;
        }

        // Allow workspace setup to finish and hand off to AI onboarding
        if (currentPath === '/onboarding' || currentPath === '/ai-onboarding') {
          return children;
        }

        // If user is on public routes (login, signup, etc.), redirect to dashboard
        const publicRoutes = [
          '/',
          '/sign-up',
          '/forgot-password',
          '/verify-otp',
          '/connect-linkedin-account',
          '/onboarding',
          '/billing',
          '/verify-email',
          '/change-password',
          '/successful',
          '/accept-workspace-invite',
        ];

        if (publicRoutes.includes(currentPath)) {
          // const hasSeenTour = localStorage.getItem('hasSeenTour');
          // return <Navigate to={hasSeenTour ? '/dashboard' : '/get-started'} replace />;
          return <Navigate to={'/dashboard'} replace />;
        }

        const isRouteAllowed = (currentPath, allowedPages) => {
          if (!allowedPages || allowedPages.length === 0) {
            return true;
          }

          // Always allow automation routes
          if (currentPath.includes('automation')) {
            return true;
          }

          // Always allow csv-upload route (guarded inside component)

          if (currentPath === '/csv-upload') {
            return true;
          }

          // First check for exact match
          if (allowedPages.includes(currentPath)) {
            return true;
          }

          // Check for dynamic route patterns (e.g., /lead-preview/:id)
          for (const allowedRoute of allowedPages) {
            // Convert route pattern to regex
            // Replace :param with regex pattern to match any value
            const pattern = allowedRoute
              .replace(/:[^/]+/g, '[^/]+') // Replace :id, :name, etc. with pattern
              .replace(/\//g, '\\/'); // Escape forward slashes

            const regex = new RegExp(`^${pattern}$`);

            if (regex.test(currentPath)) {
              return true;
            }
          }

          return false;
        };

        if (!isRouteAllowed(currentPath, allowedPages)) {
          return <Navigate to="/dashboard" replace />;
        }

        return children;
      }
    }
  }

  if (!requireAuth && !isLoggedIn) {
    return children;
  }

  if (!requireAuth && isLoggedIn) {
    // If user data is still loading, show the page (don't redirect yet)
    if (!authReady || loading_user || !user) {
      return children;
    }

    const userData = user.data || user;
    const { status, isEmailVerified } = userData;

    // Only redirect if user is verified and has active subscription/trial
    if (isEmailVerified && (status === 'trial_active' || status === 'subscription_active')) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default RouteGuard;
